import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { api, getUser, logout } from '@/utils/api';
import { Ticket, Settings, ClipboardList, AlertTriangle, ChevronRight } from 'lucide-react';
import { format, differenceInDays, parseISO } from 'date-fns';
import AdminNav from '@/components/AdminNav';
import DemoBanner from '@/components/DemoBanner';
import useDemoExpiry from '@/hooks/useDemoExpiry';

const getDaysRemaining = (sla_deadline) =>
  differenceInDays(parseISO(sla_deadline), new Date());

const getCompletedStages = (stages) =>
  Object.values(stages || {}).filter((s) => s.done).length;

const DaysChip = ({ days }) => {
  if (days < 0)
    return (
      <span className="flex items-center gap-1 font-semibold text-red-600">
        <AlertTriangle className="h-3 w-3" />
        {Math.abs(days)}d overdue
      </span>
    );
  if (days <= 3)
    return <span className="font-semibold text-amber-600">{days}d left</span>;
  return <span className="text-green-700">{days}d left</span>;
};

const WorkOrders = () => {
  const navigate = useNavigate();
  const user = getUser();
  useDemoExpiry();
  const [workOrders, setWorkOrders] = useState([]);
  const [statusFilter, setStatusFilter] = useState('All');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || (user.role !== 'admin' && user.role !== 'demo')) {
      navigate('/admin/login');
      return;
    }
    fetchWorkOrders();
  }, [statusFilter]);

  const fetchWorkOrders = async () => {
    try {
      setLoading(true);
      const params = statusFilter !== 'All' ? { status: statusFilter } : {};
      const res = await api.get('/work-orders', { params });
      setWorkOrders(res.data);
    } catch {
      toast.error('Failed to fetch work orders');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const getRowClass = (days) => {
    if (days < 0) return 'bg-red-50 border-red-200 hover:bg-red-100';
    if (days <= 3) return 'bg-amber-50 border-amber-200 hover:bg-amber-100';
    return 'hover:bg-muted/30';
  };

  return (
    <div className="min-h-screen bg-background">
      <AdminNav
        title="Work Orders"
        titleIcon={ClipboardList}
        navItems={[
          { label: 'Dashboard', icon: Ticket, onClick: () => navigate('/admin/dashboard'), testId: 'dashboard-nav-btn' },
          { label: 'Settings', icon: Settings, onClick: () => navigate('/admin/settings'), testId: 'settings-nav-btn' },
        ]}
        username={user?.first_name}
        onLogout={handleLogout}
      />
      <DemoBanner />

      <div className="container mx-auto px-4 sm:px-6 py-6 lg:py-8">
        <Card className="border-border shadow-lg">
          <CardHeader>
            <CardTitle className="text-xl sm:text-2xl font-heading">
              Replacement Work Orders
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Status filter */}
            <div className="flex flex-wrap gap-2 mb-6" data-testid="work-order-filter-tabs">
              {['All', 'Open', 'Completed'].map((s) => (
                <Button
                  key={s}
                  variant={statusFilter === s ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setStatusFilter(s)}
                  data-testid={`wo-filter-${s.toLowerCase()}`}
                  className="h-9"
                >
                  {s}
                </Button>
              ))}
            </div>

            {loading ? (
              <div className="text-center py-12 text-muted-foreground">Loading work orders...</div>
            ) : workOrders.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">No work orders found</div>
            ) : (
              <>
                {/* Desktop table */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full" data-testid="work-orders-table">
                    <thead className="border-b border-border">
                      <tr className="text-left">
                        <th className="pb-3 font-semibold text-sm">Work Order ID</th>
                        <th className="pb-3 font-semibold text-sm">Ticket ID</th>
                        <th className="pb-3 font-semibold text-sm">Type</th>
                        <th className="pb-3 font-semibold text-sm">Customer</th>
                        <th className="pb-3 font-semibold text-sm">SLA Deadline</th>
                        <th className="pb-3 font-semibold text-sm">Days Remaining</th>
                        <th className="pb-3 font-semibold text-sm">Stages</th>
                        <th className="pb-3 font-semibold text-sm">Status</th>
                        <th className="pb-3 w-8"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {workOrders.map((wo) => {
                        const days = getDaysRemaining(wo.sla_deadline);
                        const completed = getCompletedStages(wo.stages);
                        return (
                          <tr
                            key={wo.work_order_id}
                            className={`border-b border-border last:border-0 cursor-pointer transition-colors ${getRowClass(days)}`}
                            onClick={() => navigate(`/admin/work-orders/${wo.work_order_id}`)}
                            data-testid="work-order-row"
                          >
                            <td className="py-4 text-sm font-mono">{wo.work_order_id}</td>
                            <td className="py-4 text-sm font-mono text-muted-foreground">{wo.parent_ticket_id.substring(0, 8)}</td>
                            <td className="py-4 text-sm"><Badge variant="outline">{wo.replacement_type}</Badge></td>
                            <td className="py-4 text-sm">{wo.customer_name}</td>
                            <td className="py-4 text-sm">{format(parseISO(wo.sla_deadline), 'MMM d, yyyy')}</td>
                            <td className="py-4 text-sm"><DaysChip days={days} /></td>
                            <td className="py-4 text-sm text-muted-foreground">{completed}/7</td>
                            <td className="py-4">
                              <Badge className={wo.status === 'Completed' ? 'bg-green-100 text-green-800 border-green-200' : 'bg-yellow-100 text-yellow-800 border-yellow-200'} data-testid={`wo-status-${wo.work_order_id}`}>
                                {wo.status}
                              </Badge>
                            </td>
                            <td className="py-4"><ChevronRight className="h-4 w-4 text-muted-foreground" /></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Mobile cards */}
                <div className="md:hidden space-y-3" data-testid="work-orders-mobile-list">
                  {workOrders.map((wo) => {
                    const days = getDaysRemaining(wo.sla_deadline);
                    const completed = getCompletedStages(wo.stages);
                    return (
                      <div
                        key={wo.work_order_id}
                        className={`border rounded-xl p-4 cursor-pointer active:opacity-80 ${getRowClass(days)}`}
                        onClick={() => navigate(`/admin/work-orders/${wo.work_order_id}`)}
                        data-testid="work-order-card-mobile"
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <span className="text-sm font-mono font-bold">{wo.work_order_id}</span>
                          <Badge className={wo.status === 'Completed' ? 'bg-green-100 text-green-800 border-green-200' : 'bg-yellow-100 text-yellow-800 border-yellow-200'}>
                            {wo.status}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline" className="text-xs">{wo.replacement_type}</Badge>
                          <span className="text-sm font-medium">{wo.customer_name}</span>
                        </div>
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span>SLA: {format(parseISO(wo.sla_deadline), 'MMM d, yyyy')}</span>
                          <DaysChip days={days} />
                        </div>
                        <div className="mt-2 text-xs text-muted-foreground">{completed}/7 stages complete</div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default WorkOrders;
